import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ActionType, ModelType, UiActions } from '../reference-tree-view/reference.enum';
import { Store } from '@ngrx/store';
import { cloneDeep, debounce, difference, filter, isEqual } from 'lodash-es';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { MessageService } from 'primeng/api';
import { Observable, Subscription } from 'rxjs';
import { picklist, searchFields } from 'src/app/STORE/selectors/propertypane.selector';
import { TranslateService } from '@ngx-translate/core';
import { pageTableType } from 'src/app/pages/prototyping/screens/screens.component';
import { ReferenceUtilService } from 'src/app/components/reference-tree-view/reference-util.service';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { UtilService } from 'src/app/services/util.service';
import { AppGlobalService } from 'src/app/services/app.global.service';

@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-pickup-list',
  templateUrl: './pickup-list.component.html',
  styleUrls: ['./pickup-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PickupListComponent),
      multi: true
    }
  ],
  standalone: false //NOSONAR
})
export class PickupListComponent implements OnInit, OnDestroy, OnChanges {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private renderer = inject(Renderer2);
  private appGlobalService = inject(AppGlobalService);
  private util = inject(UtilService);
  private loader = inject(AppLoaderService);
  private elementRef = inject(ElementRef);
  private referenceUtil = inject(ReferenceUtilService);
  private messageService = inject(MessageService);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private cd = inject(ChangeDetectorRef);

  @Input() element: any;
  @Input() fieldIdMap: any;
  @Input() formData: any;
  @Input() pickList: any;
  @Input() disableField = false;
  @Input() currNodeType: any;
  @Input() currentScreen: any;
  @Input() techStackData: any;
  @Input() isSidFieldAvlInIndex = false;
  @Input() errorObj: any;

  @ViewChild('mSelect') mSelect;
  private modelValue: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  subscriptions: Subscription[] = [];
  allFields: any[] = [];
  source: any[] = [];
  target: any[] = [];
  showPickList = false;
  selected: any[];
  targetBackup: any[];
  fromSave = false;
  lists: any;
  currentTablename: any;
  label: any;
  pageName: any;
  menulabel: any;

  sortOrderOptions = [
    { label: 'asc', value: 'asc' },
    { label: 'desc', value: 'desc' }
  ];

  get isBetaAccessPrivilegeConsumable(): boolean {
    return this.appGlobalService.betaAccessPrivilegeForApp;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pickList || changes.isSidFieldAvlInIndex || changes.fieldIdMap) {
      setTimeout(() => {
        this.setOptions();
      }, 500);
    }
  }
  setOptions() {
    this.pageName = this.currentScreen?.pageName;
    if (this.pickList) {
      // only for table properties lookup field
      if (this.element.lookupTo === 'listoffields' && this.element.name === 't_unique_for_lookup') {
        this.label = this.translate.instant('UNIQUE_REC_FOR_LOOKUP');
        this.lists = this.pickList?.uniqueFields ? this.pickList?.uniqueFields : [];
        this.allFields = [...this.lists];
      } else if (this.element.lookupTo === 'listoffields' && this.element.name === 't_mandatory_filters') {
        this.label = this.translate.instant('MANDATORY_INPUT_PARAMETERS');
        this.lists = this.pickList?.uniqueFields ? this.pickList?.uniqueFields : [];
        const values = [];
        if (this.formData?.t_optional_filters) {
          const mFlds = this.formData?.t_optional_filters?.split(',');
          this.allFields = [...this.lists.filter((f) => !mFlds.includes(f.sid))];
        } else {
          this.allFields = [...this.lists];
        }
      } else if (this.element.lookupTo === 'listoffields' && this.element.name === 't_optional_filters') {
        this.label = this.translate.instant('OPTIONAL_INPUT_PARAMETERS');
        this.lists = this.pickList?.uniqueFields ? this.pickList?.uniqueFields : [];
        const values = [];
        if (this.formData?.t_mandatory_filters) {
          const oFlds = this.formData?.t_mandatory_filters?.split(',');
          this.allFields = [...this.lists.filter((f) => !oFlds.includes(f.sid))];
        } else {
          this.allFields = [...this.lists];
        }
      }
      if (
        this.element.lookupTo === 'listoffields' &&
        this.element.name !== 'groupsOnColumn' &&
        this.element.name !== 'sortFields'
      ) {
        const lists = this.pickList?.lookupFields ? this.pickList?.lookupFields : [];
        this.lists = lists.map((f) => {
          if (f.name === 'Sid') {
            f = { ...f, ...{ isDisabled: true } };
          }
          return f;
        });
      } else if (
        this.element.lookupTo === 'listoffields' &&
        (this.element.name === 'groupsOnColumn' || this.element.name === 'sortFields')
      ) {
        this.lists = this.pickList?.gridFields;
        const searchableFields = this.pickList?.searchableFields;
        const indexedFields = this.pickList?.indexedFields;

        this.allFields = [...this.lists]
          .filter((field) => !this.pickList.transientFields?.some((f) => f.sid === field.sid)) // Exclude transient fields
          .map((field) => {
            const isIndexed = indexedFields?.some((i) => field.sid === i);
            let sortOrder;

            if (this.element.name === 'sortFields') {
              sortOrder =
                this.formData[this.element.name]?.find((sortFields) => sortFields.sid === field.sid)?.type || 'asc';
            }

            return {
              ...field,
              searchable: true, // only searchable fields will be listed so no need of extra condition
              indexed: !!isIndexed,
              sortOrder: sortOrder
            };
          });

        this.label = this.element?.title;
      } else if (this.element.lookupTo === 'listoffieldsbutton') {
        const lists = this.pickList?.buttonFields ? this.pickList?.buttonFields : [];
        this.lists = lists.map((f) => {
          if (f.name === 'Sid') {
            f = { ...f, ...{ isDisabled: true } };
          }
          return f;
        });
        this.allFields = [...this.lists];
      } else if (this.element.lookupTo === 'listofsearchablefields') {
        if (this.currentScreen.pageTableType === pageTableType.QUERY_VIEW) {
          this.lists = this.pickList?.noImgFAFields ?? [];
        } else {
          this.lists = this.pickList?.searchableFields ? this.pickList?.searchableFields : [];
        }
        this.allFields = [...this.lists];
      } else if (this.element.lookupTo === 'listoftables') {
        this.lists = this.pickList?.tables ? this.pickList?.tables : [];
      } else if (this.element.lookupTo === 'listoffieldsforprimary') {
        this.lists = this.pickList?.primaryFields ? this.pickList?.primaryFields : [];
      } else if (this.element.lookupTo === 'allfields') {
        this.lists = this.pickList?.allFields ? this.pickList?.allFields : [];
      } else if (this.element.lookupTo === 'listoffieldsExpImgFA') {
        this.lists = this.pickList?.noImgFAFields ? this.pickList?.noImgFAFields : [];
      } else if (this.element.lookupTo === 'indexFields') {
        this.lists = this.pickList?.indexFields ? this.pickList?.indexFields : [];
        if ((!this.formData || !this.disableField) && !this.isSidFieldAvlInIndex) {
          this.lists = this.lists.filter((obj) => obj.name !== 'Sid');
        }
      }
    }
    if (this.element.lookupTo === 'listofsearchablefields') {
      this.allFields = [...this.lists];
      this.label = this.element?.title;
    } else if (
      this.element.name !== 't_unique_for_lookup' &&
      this.element.name !== 't_mandatory_filters' &&
      this.element.name !== 't_optional_filters' &&
      this.element.name !== 'groupsOnColumn' &&
      this.element.name !== 'sortFields' &&
      this.element.name !== 'fields'
    ) {
      this.allFields = [];
      this.label = this.element?.title;
      this.menulabel = this.formData?.label;
    }
    if (this.element.possibleValues) {
      let flds;
      if (this.formData[this.element.possibleValuesFrom]) {
        flds = this.formData[this.element.possibleValuesFrom].split(',');
      } else {
        flds = [];
      }
      let values = [...flds];
      if (this.element.name === 't_mFilters') {
        if (this.formData?.t_oFilters) {
          const opFlds = this.formData?.t_oFilters.split(',');
          values = flds.filter((fld) => !opFlds.includes(fld));
        }
      } else if (this.element.name === 't_oFilters') {
        if (this.formData?.t_mFilters) {
          const manFlds = this.formData?.t_mFilters.split(',');
          values = flds.filter((fld) => !manFlds.includes(fld));
        }
      }

      if (values && values.length) {
        values.forEach((val) => {
          const obj = this.lists?.find((fld) => fld.sid === val);
          if (obj && (this.allFields.filter((fld) => fld.sid === val).length === 0 || this.allFields.length === 0)) {
            this.allFields.push(obj);
          }
        });
      }
    } else if (
      this.element.name !== 't_unique_for_lookup' &&
      this.element.name !== 't_mandatory_filters' &&
      this.element.name !== 't_optional_filters'
    ) {
      this.allFields = [...(this.lists ?? [])];
    }
    if (this.fieldIdMap) {
      const updateName = (f) => (this.fieldIdMap[f.sid] ? { ...f, name: this.fieldIdMap[f.sid] } : f);
      this.lists = this.lists?.map(updateName);
      this.allFields = this.allFields?.map(updateName);
      this.source = this.source?.map(updateName);
      this.target = this.target?.map(updateName);
      this.selected = this.selected?.map(updateName);
    }
    if (this.formData?.[this.element.name]) {
      this.writeValue(this.formData[this.element.name]);
    }
  }

  ngOnInit(): void {
    this.setOptions();
  }

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.setValueToPickList(value);
    this.modelValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    // this.disabled = isDisabled;
  }

  togglePickList(event?: any) {
    this.currentTablename = this.formData?.name;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      this.target = [...this.selected];
      this.source = difference(this.allFields, this.target);
      this.targetBackup = [...this.target];
    }
    this.showPickList = !this.showPickList;
  }

  setValueToPickList(value) {
    this.target = [];
    this.selected = [];
    if (value) {
      const values = typeof value === 'string' ? value.split(',') : value;
      if (values.length > 0) {
        values.forEach((e) => {
          const sid = typeof e === 'object' ? e.sid : e;
          const elem = this.allFields.filter((o) => o.sid === sid);
          if (elem[0]) {
            this.target.push(elem[0]);
          }
        });
        this.source = difference(this.allFields, this.target);
      } else {
        this.source = [...this.allFields];
        this.target = [];
      }
    } else {
      this.source = [...this.allFields];
      this.target = [];
    }
    if (!this.target) {
      this.target = [];
    }
    this.selected = [...this.target];
  }

  onMoveToTarget(event) {
    console.log(event);
    this.source = [...this.source];
    this.target = [...this.target];
  }

  onMoveToSource(event) {
    console.log(event);
    this.source = [...this.source];
    this.target = [...this.target];
  }

  onTargetReorder(event) {
    console.log(event);
    this.source = [...this.source];
    this.target = [...this.target];
  }

  setValueToForm(list) {
    let value;
    if (this.element.name === 'sortFields') {
      value = list.map(({ sid, sortOrder = 'asc', fkTableSid, fkId, fkAppId }) => ({
        sid,
        type: sortOrder,
        fkTableSid,
        fkId,
        fkAppId
      }));
    } else {
      value = list.map((a) => a.sid).join();
    }
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  savePicklist() {
    this.selected = [...this.target];
    this.setValueToForm(this.target);
    this.fromSave = true;
    this.togglePickList();
  }
  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    let sticky;
    if (status === 'error') {
      sticky = true;
    } else {
      sticky = false;
    }
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail,
      sticky: sticky
    });
  }

  debounceValChanges = debounce(this.setValueToForm.bind(this), 1000);

  updateValues(event) {
    if (this.element.lookupTo === 'listoffieldsExpImgFA' && event.value.length > 16) {
      this.mSelect.value = [];
      this.showMessage('error', 'Maximum number of fields indexed is 16');
      event.stopPropagation();
      return;
    }
    this.checkForQueryRefs();
    // this.setValueToForm(this.selected);
    // this.debounceValChanges(this.selected);
  }
  callValidateQueryRefs(oldFilters, property) {
    const newFilters = this.selected.map((s) => {
      return s.sid;
    });
    if (oldFilters.length > newFilters.length) {
      let cloneOldFilters = cloneDeep(oldFilters);
      const removedFilters = [...cloneOldFilters.filter((o) => !newFilters.includes(o))];

      // If 2 or more fields are removed, validate all together
      if (removedFilters.length >= 2) {
        this.validateMultipleQueryRefs(removedFilters, oldFilters, property);
      } else {
        // Single removal - validate individual field
        this.validateQueryRefs(removedFilters[0], oldFilters, property);
      }
    } else {
      this.debounceValChanges(this.selected);
    }
  }

  checkForQueryRefs() {
    if (
      this.element.name === 't_mandatory_filters' ||
      this.element.name === 't_optional_filters' ||
      this.element.name === 't_unique_for_lookup'
    ) {
      if (this.element.name === 't_mandatory_filters' && this.formData?.t_mandatory_filters) {
        this.callValidateQueryRefs(this.formData?.t_mandatory_filters.split(','), 'queryMapping');
      } else if (this.element.name === 't_optional_filters' && this.formData?.t_optional_filters) {
        this.callValidateQueryRefs(this.formData?.t_optional_filters.split(','), 'queryMapping');
      } else if (this.element.name === 't_unique_for_lookup' && this.formData?.t_unique_for_lookup) {
        this.callValidateQueryRefs(this.formData?.t_unique_for_lookup.split(','), 'lookupTemplate');
      } else {
        this.debounceValChanges(this.selected);
      }
    } else {
      this.debounceValChanges(this.selected);
    }
  }

  validateQueryRefs(fieldId, filters, property) {
    this.loader.show();
    let actionType: ActionType = ActionType.MANDATORY_FIELD_REMOVE;
    if (property === 'queryMapping') {
      if (this.element?.name === 't_mandatory_filters') {
        actionType = ActionType.MANDATORY_FIELD_REMOVE;
      } else if (this.element?.name === 't_optional_filters') {
        actionType = ActionType.OPTIONAL_FIELD_REMOVE;
      }
    } else if (property === 'lookupTemplate' || this.element?.name === 't_unique_for_lookup') {
      actionType = ActionType.UNIQUE_FIELD_REMOVE;
    }

    this.referenceUtil
      .handleWithReferences({
        payload: {
          modelId: fieldId,
          modelType: ModelType.FIELD,
          actionType: actionType,
          action: UiActions.DELETE
        },
        node: { name: this.appGlobalService.get('appmetaInfo')[fieldId], type: ModelType.FIELD },
        filter: (refs: any[]) =>
          (refs || []).filter(
            (r: any) =>
              r.modelType !== 'MANDATORY_INPUT_FILTERS_DEFINITION' && r.modelType !== 'OPTIONAL_FILTERS_DEFINITION'
          ),
        onConfirm: () => {
          this.debounceValChanges(this.selected);
        },
        onNoReferences: () => {
          this.debounceValChanges(this.selected);
        },
        onCancel: () => {
          this.writeValue(filters.join(','));
          this.cd.detectChanges();
        },
        onError: () => {
          // ignore and keep current selection
        }
      })
      .catch(() => {
        /* ignored */
      })
      .finally(() => this.loader.hide());
  }

  validateMultipleQueryRefs(removedFieldIds: string[], oldFilters: string[], property: string) {
    this.loader.show();
    let actionType: ActionType = ActionType.MANDATORY_FIELD_REMOVE;
    if (property === 'queryMapping') {
      if (this.element?.name === 't_mandatory_filters') {
        actionType = ActionType.MANDATORY_FIELD_REMOVE;
      } else if (this.element?.name === 't_optional_filters') {
        actionType = ActionType.OPTIONAL_FIELD_REMOVE;
      }
    } else if (property === 'lookupTemplate' || this.element?.name === 't_unique_for_lookup') {
      actionType = ActionType.UNIQUE_FIELD_REMOVE;
    }

    // Build array of payload objects for handleWithReferences
    const payloadArray = removedFieldIds.map((fieldId: string) => ({
      modelId: fieldId,
      modelType: ModelType.FIELD,
      actionType: actionType,
      action: UiActions.DELETE,
      appId: this.appGlobalService.get('appId')
    }));

    // Use handleWithReferences with array of payloads
    this.referenceUtil
      .handleWithReferences({
        payload: payloadArray,
        node: { name: removedFieldIds.join(', '), type: ModelType.FIELD },
        filter: (refs: any[]) =>
          (refs || []).filter(
            (r: any) =>
              r.modelType !== 'MANDATORY_INPUT_FILTERS_DEFINITION' && r.modelType !== 'OPTIONAL_FILTERS_DEFINITION'
          ),
        onConfirm: () => {
          this.debounceValChanges(this.selected);
        },
        onNoReferences: () => {
          this.debounceValChanges(this.selected);
        },
        onCancel: () => {
          this.writeValue(oldFilters.join(','));
          this.cd.detectChanges();
        },
        onError: () => {
          // ignore and keep current selection
        }
      })
      .catch(() => {
        /* ignored */
      })
      .finally(() => this.loader.hide());
  }

  closeDialog(event) {
    if (!isEqual(this.target, this.targetBackup) && !this.fromSave) {
      this.confirmationDialogService.confirm({
        message: 'You have unsaved changes. Are you sure you want to leave this page?',
        accept: () => {
          this.showPickList = false;
          this.target = [...this.targetBackup];
        },
        reject: () => {
          this.showPickList = true;
        }
      });
    } else {
      this.showPickList = false;
    }
    this.fromSave = false;
  }

  setSortOrder(property?: any, sortOrder = 'asc', event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    if (this.element.name === 'sortFields') {
      const existingField = this.target.find((f) => f.sid === property.sid);
      if (existingField) {
        existingField.sortOrder = sortOrder;
      } else {
        this.target.push({ ...property, sortOrder });
      }
    }
  }

  onDropdownClick(event: MouseEvent, mSelect: any) {
    const isGroupOrSort = this.element?.name === 'groupsOnColumn' || this.element?.name === 'sortFields';
    if (isGroupOrSort) {
      this.togglePickList(event);
      event.stopPropagation();
      event.preventDefault();
      mSelect.hide();
    }
  }

  getSearchLabel(fields: any): string {
    const isGroupOrSort = this.element?.name === 'groupsOnColumn' || this.element?.name === 'sortFields';
    const isQueryView = this.currentScreen?.pageTableType === pageTableType.QUERY_VIEW;
    const isElastic = this.techStackData?.textSearch === 'Elastic Search';

    if (isGroupOrSort && !fields?.searchable && isElastic && !isQueryView) {
      return '(Not Searchable)';
    }
    if (isGroupOrSort && !fields?.indexed && !isElastic && !isQueryView) {
      return '(Not Indexed)';
    }
    return '';
  }

  removeSelectedItem(item: any, event: Event) {
    this.selected = this.selected.filter((i) => i.sid !== item.sid);
    event.stopPropagation();
    this.checkForQueryRefs();
  }

  ngOnDestroy(): void {
    //AutoUnsubscribe
    $(this.elementRef.nativeElement).remove();
  }

  get multiSelectDisplay() {
    if (this.currNodeType === 'button') {
      return this.selected?.length > 5 ? 'comma' : 'chip';
    }

    return 'chip';
  }

  showPopup: boolean = false;
  toggleWarningPopup() {
    this.showPopup = !this.showPopup;
  }
}


